// Modal-stack render for the inventory orchestrator. Extracted from
// `ManufacturingInventoryPanel.tsx` to keep that file under the
// file-size hygiene cap once the dev-sync 184bc79f delete-with-restore
// flow added the per-material confirm modal. All state still lives in
// the orchestrator — this component is purely presentational
// composition that returns a fragment of <>{ ...modals }</>.
import { EXAMPLE_BULK_IMPORT_CSV, downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ManufacturingBulkImportModal } from "./ManufacturingBulkImportModal";
import { ManufacturingInventoryTemplateConfirm } from "./ManufacturingInventoryTemplateConfirm";
import { ManufacturingMaterialDeleteConfirm } from "./ManufacturingMaterialDeleteConfirm";
import { ManufacturingMaterialPermanentDeleteConfirm } from "./ManufacturingMaterialPermanentDeleteConfirm";
import { ManufacturingPanelModal } from "./ManufacturingPanelModal";
import type { CreateMaterialPayload } from "./useManufacturingCreateMaterial";
import type { UpdateMaterialPayload } from "./useManufacturingUpdateMaterial";
import type { DeletionConfirmCopy } from "./materialDeletionPendingCopy";
import type { MfgModalKind } from "./mfgPanelTypes";

type SaveResult = { ok: true } | { ok: false; error?: string };

type BulkImportSummary = Parameters<
  React.ComponentProps<typeof ManufacturingBulkImportModal>["onImported"]
>[0];

type Props = {
  // Material edit/create modal
  openModal: MfgModalKind | null;
  setOpenModal: (next: MfgModalKind | null) => void;
  editingMaterial: MfgMaterial | undefined;
  existingCategories: ReadonlyArray<string>;
  existingUnits: ReadonlyArray<string>;
  existingShippingUnits: ReadonlyArray<string>;
  canEditCode: boolean;
  onCreateMaterial: (payload: CreateMaterialPayload) => Promise<SaveResult>;
  onUpdateMaterial: (payload: UpdateMaterialPayload) => Promise<SaveResult>;

  // Bulk import modal
  bulkOpen: boolean;
  onBulkImportClose: () => void;
  existingMaterials: ReadonlyArray<MfgMaterial>;
  onBulkImported: (summary: BulkImportSummary) => void;

  // Template confirm modal
  templateConfirmOpen: boolean;
  onTemplateCancel: () => void;
  onTemplateConfirmed: () => void;

  // Delete / restore confirm modal
  deletionPendingCopy: DeletionConfirmCopy | null;
  deletionBusy: boolean;
  onDeletionCancel: () => void;
  onDeletionConfirm: () => void;

  // Permanent delete (inactive materials)
  permanentDeleteMaterial: MfgMaterial | null;
  permanentDeleteBusy: boolean;
  permanentDeleteError: string | null;
  permanentDeleteBlockers: ReadonlyArray<{ code: string; message: string }>;
  onPermanentDeleteCancel: () => void;
  onPermanentDeleteConfirm: () => void;
};

export function ManufacturingInventoryPanelModals(props: Props) {
  return (
    <>
      {props.openModal ? (
        <ManufacturingPanelModal
          key={props.editingMaterial?.id ?? "new"}
          openModal={props.openModal}
          setOpenModal={props.setOpenModal}
          editingMaterial={props.editingMaterial}
          existingCategories={props.existingCategories}
          existingUnits={props.existingUnits}
          existingShippingUnits={props.existingShippingUnits}
          canEditCode={props.canEditCode}
          onCreateMaterial={props.onCreateMaterial}
          onUpdateMaterial={props.onUpdateMaterial}
        />
      ) : null}

      {props.bulkOpen ? (
        <ManufacturingBulkImportModal
          existingMaterials={props.existingMaterials}
          onClose={props.onBulkImportClose}
          onImported={props.onBulkImported}
        />
      ) : null}

      <ManufacturingInventoryTemplateConfirm
        open={props.templateConfirmOpen}
        onCancel={props.onTemplateCancel}
        onConfirm={() => {
          downloadCsv("mfg-materials-import-template.csv", EXAMPLE_BULK_IMPORT_CSV);
          props.onTemplateConfirmed();
        }}
      />

      {props.deletionPendingCopy ? (
        <ManufacturingMaterialDeleteConfirm
          open={true}
          variant={props.deletionPendingCopy.variant}
          title={props.deletionPendingCopy.title}
          body={props.deletionPendingCopy.body}
          confirmLabel={props.deletionPendingCopy.confirmLabel}
          busy={props.deletionBusy}
          onCancel={props.onDeletionCancel}
          onConfirm={props.onDeletionConfirm}
        />
      ) : null}

      <ManufacturingMaterialPermanentDeleteConfirm
        open={!!props.permanentDeleteMaterial}
        material={props.permanentDeleteMaterial}
        busy={props.permanentDeleteBusy}
        error={props.permanentDeleteError}
        blockers={props.permanentDeleteBlockers}
        onCancel={props.onPermanentDeleteCancel}
        onConfirm={props.onPermanentDeleteConfirm}
      />
    </>
  );
}
